import type { PhisherConfig } from '../models/PhisherConfig';
import { buildReportUrl } from '../models/PhisherConfig';
import type {
  ReportPayload,
  ReportResult,
  EmailAnalysis,
  CampaignInfo,
  UserInfo,
} from '../models/ReportPayload';
import { HeaderParser } from '../helpers/HeaderParser';
import { UserInfoHelper } from '../helpers/UserInfoHelper';

/**
 * Service for submitting phishing reports to the API.
 * Ports RawamReporter.cs functionality.
 */
export class ReportService {
  private static readonly TIMEOUT_MS = 60000; // 60 seconds

  /**
   * Submit a phishing report based on reportAction config.
   * Matches VSTO Ribbon.cs behavior:
   *   "Ticket" = API only
   *   "Email"  = Email only (compose forward to SOC)
   *   "Both"   = API first, then email. If API fails, fallback to email.
   */
  static async submitReport(
    mailbox: Office.Mailbox,
    item: Office.MessageRead,
    config: PhisherConfig,
    campaignInfo: CampaignInfo,
    analysis: EmailAnalysis,
    emlContent?: ArrayBuffer
  ): Promise<ReportResult> {
    try {
      // Build the report payload
      const payload = await this.buildPayload(mailbox, item, config, campaignInfo, analysis);

      const action = (config.reportAction || 'Both').toLowerCase();
      let doApi = action === 'ticket' || action === 'both';
      let doEmail = action === 'email' || action === 'both';

      let apiOk = false;
      let emailOk = false;
      let apiError = '';

      // Step 1: Try API submission
      if (doApi) {
        // Use empty GUID for non-campaign emails (matches VSTO behavior)
        const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
        const campaignId = campaignInfo.parsedId || campaignInfo.rawId || EMPTY_GUID;
        const reportUrl = buildReportUrl(config, campaignId);
        const apiResult = await this.sendMultipartReport(reportUrl, config, payload, emlContent);
        apiOk = apiResult.success;
        if (!apiOk) {
          apiError = apiResult.message || 'API submission failed';
          // Note: Web plugin does NOT fallback to email (unlike VSTO)
          // because web plugin can only open compose window, not send silently
        }
      }

      // Step 2: Send email to SOC
      if (doEmail) {
        emailOk = await this.sendEmailReport(mailbox, item, config, campaignInfo, payload);
      }

      // Determine result (matches VSTO logic)
      if (apiOk && emailOk) {
        return { success: true, message: 'Report submitted to security team and email sent.' };
      } else if (apiOk) {
        return { success: true, message: 'Report submitted to security team.' };
      } else if (emailOk) {
        return { success: true, message: 'Report email composed. Please send it to complete the report.' };
      } else {
        return {
          success: false,
          message: 'Failed to submit report',
          error: apiError || 'Could not submit report via API or email.',
        };
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      return {
        success: false,
        message: 'Failed to submit report',
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Send report as email to SOC via Office.js displayNewMessageForm.
   * Matches VSTO ReportComposer.CreateReportEmail behavior.
   * Opens a new email compose window addressed to SOC with the report body.
   */
  private static async sendEmailReport(
    mailbox: Office.Mailbox,
    item: Office.MessageRead,
    config: PhisherConfig,
    campaignInfo: CampaignInfo,
    payload: ReportPayload
  ): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        const isCampaign = campaignInfo.isCampaign;
        const subjectPrefix = isCampaign
          ? '[TRAINING PHISH \u2013 DO NOT INVESTIGATE]'
          : '[POTENTIAL PHISH]';
        const subject = `${subjectPrefix} ${item.subject || '(No subject)'}`;

        // Build email body matching VSTO format
        const bodyParts: string[] = [];

        if (isCampaign) {
          bodyParts.push(`CampaignUserId: ${campaignInfo.rawId || 'N/A'}`);
          bodyParts.push('NOTE: This is a training/simulated phishing report. No investigation needed.\n');
        }

        bodyParts.push(payload.ForwardBodyText);

        const body = bodyParts.join('\n');

        // Use Office.js to open compose window addressed to SOC
        mailbox.displayNewMessageForm({
          toRecipients: [config.toAddress],
          subject: subject,
          htmlBody: `<pre style="font-family: Consolas, monospace; font-size: 12px;">${this.escapeHtml(body)}</pre>`,
        });

        // displayNewMessageForm opens compose window - user must click Send
        resolve(true);
      } catch (error) {
        console.error('Error composing email report:', error);
        resolve(false);
      }
    });
  }

  /**
   * Escape HTML special characters for safe insertion into htmlBody.
   */
  private static escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Build the JSON payload for the report.
   */
  private static async buildPayload(
    mailbox: Office.Mailbox,
    item: Office.MessageRead,
    config: PhisherConfig,
    campaignInfo: CampaignInfo,
    analysis: EmailAnalysis
  ): Promise<ReportPayload> {
    // Get user info
    const userInfo = UserInfoHelper.getUserInfo(mailbox);
    const reporterEmail = await UserInfoHelper.getReporterEmail(mailbox, item);

    // Get headers
    const headers = await HeaderParser.getHeaders(mailbox);

    // Build forward body text (detailed report)
    const forwardBody = this.buildForwardBodyText(userInfo, config, analysis, headers, item);

    // Format dates
    const now = new Date();
    const sentOn = item.dateTimeCreated ? new Date(item.dateTimeCreated) : now;
    const receivedOn = item.dateTimeModified ? new Date(item.dateTimeModified) : now;

    const payload: ReportPayload = {
      EventType: 'ReportPhish',
      CampaignUserIdRaw: campaignInfo.rawId,
      CampaignUserId: campaignInfo.parsedId,
      IsCampaign: campaignInfo.isCampaign,
      ReporterEmail: reporterEmail || userInfo.email,
      ReportedItemType: 'MailItem',
      Timestamps: {
        ClientUtc: now.toISOString(),
        SentOn: sentOn.toISOString(),
        ReceivedOn: receivedOn.toISOString(),
      },
      Mail: {
        Subject: item.subject || '',
        From: item.from?.emailAddress || '',
        DisplayFrom: item.from?.displayName || '',
        To: this.formatRecipients(item.to),
        Cc: this.formatRecipients(item.cc),
        Bcc: '', // BCC not accessible in read mode
        Headers: headers,
      },
      ForwardBodyText: forwardBody,
    };

    return payload;
  }

  /**
   * Format recipients array to string.
   */
  private static formatRecipients(recipients: Office.EmailAddressDetails[] | undefined): string {
    if (!recipients || recipients.length === 0) {
      return '';
    }
    return recipients
      .map((r) => (r.displayName ? `"${r.displayName}" <${r.emailAddress}>` : r.emailAddress))
      .join('; ');
  }

  /**
   * Build the detailed forward body text for the report.
   * Matches the VSTO plugin's format.
   */
  private static buildForwardBodyText(
    userInfo: UserInfo,
    config: PhisherConfig,
    analysis: EmailAnalysis,
    headers: string,
    item: Office.MessageRead
  ): string {
    const sections: string[] = [];

    // User information
    sections.push(UserInfoHelper.buildUserInfoText(userInfo));
    sections.push('');

    // System info
    sections.push(UserInfoHelper.getSystemInfo());
    sections.push('');

    // URLs and attachments analysis
    sections.push(analysis.summaryText);
    sections.push('');

    // Email headers
    sections.push('=== Email Headers ===');
    sections.push(headers || '(Headers not available)');
    sections.push('');

    // Plugin details
    sections.push(UserInfoHelper.buildPluginDetailsText(config.pluginVersion, config.reportsCounter));
    sections.push('');

    // Original email body (plain text)
    sections.push('=== Original Email Body ===');
    // Note: We can't easily get plain text body in Office.js, this would need body.getAsync
    sections.push(`Subject: ${item.subject || '(No subject)'}`);

    return sections.join('\n');
  }

  /**
   * Send report as multipart/form-data.
   * Matches the VSTO HttpMultipartHelper behavior.
   */
  private static async sendMultipartReport(
    url: string,
    config: PhisherConfig,
    payload: ReportPayload,
    emlContent?: ArrayBuffer
  ): Promise<ReportResult> {
    const formData = new FormData();

    // Add metadata JSON as string field (server reads form["metadata"])
    formData.append('metadata', JSON.stringify(payload));

    // Add EML content if available
    if (emlContent) {
      const emlBlob = new Blob([emlContent], {
        type: 'application/vnd.ms-outlook',
      });
      formData.append('eml', emlBlob, 'email.eml');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          [config.apiHeaderName]: config.apiKey,
          Accept: 'application/json',
        },
        body: formData,
        signal: AbortSignal.timeout(this.TIMEOUT_MS),
      });

      if (response.ok) {
        return {
          success: true,
          message: 'Report submitted successfully',
          statusCode: response.status,
        };
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        return {
          success: false,
          message: `Server returned ${response.status}`,
          statusCode: response.status,
          error: errorText,
        };
      }
    } catch (error) {
      // Return failed result instead of throwing - allows fallback to email
      const message = error instanceof Error && error.name === 'TimeoutError'
        ? 'Request timed out'
        : 'Network error - server not reachable';
      const detail = error instanceof Error ? error.message : String(error);
      return {
        success: false,
        message,
        error: detail,
      };
    }
  }

  /**
   * Get email content as EML/MIME format.
   * Uses Office.js getAsFileAsync or REST API.
   */
  static async getEmailAsEml(item: Office.MessageRead): Promise<ArrayBuffer | null> {
    return new Promise((resolve) => {
      // Check if getAsFileAsync is available (requires Mailbox 1.8+)
      if ('getAsFileAsync' in item) {
        (item as Office.MessageRead & { getAsFileAsync: (callback: (result: Office.AsyncResult<string>) => void) => void })
          .getAsFileAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              // Result is base64 encoded
              const base64 = result.value;
              const binary = atob(base64);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
              }
              resolve(bytes.buffer);
            } else {
              console.warn('getAsFileAsync failed:', result.error?.message);
              resolve(null);
            }
          });
      } else {
        // Fallback: EML not available without REST/Graph API
        console.warn('getAsFileAsync not available, EML content will not be included');
        resolve(null);
      }
    });
  }

  /**
   * Get email body content.
   */
  static async getEmailBody(item: Office.MessageRead): Promise<{ html: string; text: string }> {
    return new Promise((resolve) => {
      const result = { html: '', text: '' };

      // Get HTML body
      item.body.getAsync(Office.CoercionType.Html, (htmlResult) => {
        if (htmlResult.status === Office.AsyncResultStatus.Succeeded) {
          result.html = htmlResult.value || '';
        }

        // Get text body
        item.body.getAsync(Office.CoercionType.Text, (textResult) => {
          if (textResult.status === Office.AsyncResultStatus.Succeeded) {
            result.text = textResult.value || '';
          }
          resolve(result);
        });
      });
    });
  }
}
